/**
 * AI Chat Service — streams chat completions from the backend proxy.
 *
 * Uses fetch (not axios) because we need ReadableStream for SSE.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

function authHeaders(): Record<string, string> {
    const token = localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface ChatMessage {
    role: 'user' | 'assistant' | 'system';
    content: string;
}

/**
 * Stream a chat completion from the Mistral Agent.
 *
 * Yields partial content strings as they arrive from the SSE stream.
 */
export async function* streamChatCompletion(
    messages: ChatMessage[],
    model?: string,
    signal?: AbortSignal,
): AsyncGenerator<string> {
    const token = localStorage.getItem('access_token');

    const response = await fetch(`${API_BASE_URL}/ai-chat/completions`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ messages, model }),
        signal,
    });

    if (!response.ok) {
        const text = await response.text();
        throw new Error(`AI Chat request failed (${response.status}): ${text}`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('No response body');

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE lines
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // keep incomplete line in buffer

        for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed || !trimmed.startsWith('data:')) continue;

            const data = trimmed.slice(5).trim();
            if (data === '[DONE]') return;

            try {
                const parsed = JSON.parse(data);

                // Check for error
                if (parsed.error) {
                    throw new Error(parsed.error);
                }

                // Extract content delta
                const delta = parsed.choices?.[0]?.delta?.content;
                if (delta) {
                    yield delta;
                }
            } catch (e) {
                // Skip non-JSON lines (e.g. empty data)
                if (e instanceof SyntaxError) continue;
                throw e;
            }
        }
    }
}

export interface ExtractedClientPayload {
    name: string;
    abbreviation: string;
    /** Primary contact email when found in source text (also derivable from first POC with email). */
    main_contact_email?: string;
    pocs: Array<{ full_name: string; phone: string; job_position: string; email?: string }>;
}

/** Mistral chat completion (server-side): parse free-form text into client + POC fields. */
export async function extractClientFromText(text: string): Promise<ExtractedClientPayload> {
    const res = await fetch(`${API_BASE_URL}/ai-chat/extract-client-from-text`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ text }),
    });
    const data = await res.json().catch(() => ({} as { error?: string }));
    if (!res.ok) {
        const msg =
            typeof data?.error === 'string' && data.error.trim()
                ? data.error
                : `Extract failed (${res.status})`;
        throw new Error(msg);
    }
    return data as ExtractedClientPayload;
}

export const aiChatService = { streamChatCompletion, extractClientFromText };


/* ──────────────────────────────────────────────
 *  Library & Document Management (RAG)
 * ────────────────────────────────────────────── */

export interface MistralLibrary {
    id: string;
    name: string;
    description?: string;
    created_at?: string;
}

export interface MistralDocument {
    id: string;
    name: string;
    created_at?: string;
    num_chunks?: number;
}

async function buildErrorMessage(res: Response, fallback: string): Promise<string> {
    try {
        const data = await res.json();
        if (typeof data?.error === 'string' && data.error.trim()) {
            return data.error;
        }
    } catch {
        // Ignore parse errors and keep fallback message.
    }
    return `${fallback} (${res.status})`;
}

/** Get the pre-configured default library ID. */
export async function getDefaultLibrary(): Promise<string> {
    const res = await fetch(`${API_BASE_URL}/ai-chat/default-library`, {
        headers: authHeaders(),
    });
    if (!res.ok) throw new Error(`Get default library failed (${res.status})`);
    const data = await res.json();
    return data.library_id;
}

/** Create a new document library. */
export async function createLibrary(name: string, description = ''): Promise<MistralLibrary> {
    const res = await fetch(`${API_BASE_URL}/ai-chat/library`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ name, description }),
    });
    if (!res.ok) throw new Error(`Create library failed (${res.status})`);
    return res.json();
}

/** List all libraries. */
export async function getLibraries(): Promise<MistralLibrary[]> {
    const res = await fetch(`${API_BASE_URL}/ai-chat/library`, {
        headers: authHeaders(),
    });
    if (!res.ok) throw new Error(`List libraries failed (${res.status})`);
    const data = await res.json();
    return data.data ?? data;
}

/** Upload a file to a library. */
export async function uploadDocument(libraryId: string, file: File): Promise<MistralDocument> {
    const form = new FormData();
    form.append('file', file);

    const res = await fetch(`${API_BASE_URL}/ai-chat/library/${libraryId}/documents`, {
        method: 'POST',
        headers: authHeaders(),
        body: form,
    });
    if (!res.ok) throw new Error(await buildErrorMessage(res, 'Upload failed'));
    return res.json();
}

/** List documents in a library. */
export async function getDocuments(libraryId: string): Promise<MistralDocument[]> {
    const res = await fetch(`${API_BASE_URL}/ai-chat/library/${libraryId}/documents`, {
        headers: authHeaders(),
    });
    if (!res.ok) throw new Error(`List documents failed (${res.status})`);
    const data = await res.json();
    return data.data ?? data;
}

/** Delete a document from a library. */
export async function deleteDocument(libraryId: string, docId: string): Promise<void> {
    const res = await fetch(`${API_BASE_URL}/ai-chat/library/${libraryId}/documents/${docId}`, {
        method: 'DELETE',
        headers: authHeaders(),
    });
    if (!res.ok) throw new Error(`Delete failed (${res.status})`);
}
